import { createPortal } from 'react-dom';
import { LocationsContext } from "store/context/LocationsContext";
import React, { useContext, useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { DEFAULT_MAP_OPTIONS } from "config/maps";
import { useMapStore } from "store/mapStore";

interface MapComponentProps {
  onEdit?: (polygon: any) => void;
  onDelete?: (id: string) => void;
  onSaveGeometry?: (id: string, newCoordinates: any) => Promise<void>;
  onMapReady?: () => void;
  isDrawing?: boolean;
  onPolygonComplete?: (coordinates: any[]) => void;
  polygons?: any[];
  setTempPolygon?: Dispatch<any>;
  setIsDrawing?: Dispatch<SetStateAction<boolean>>;
  setShowPolygonForm?: Dispatch<SetStateAction<boolean>>;
  tempPolygon?: any;
}

const MapComponent: React.FC<MapComponentProps> = ({
  onEdit,
  onDelete,
  onSaveGeometry,
  onMapReady,
  isDrawing,
  onPolygonComplete,
  polygons,
  setTempPolygon,
  setIsDrawing,
  setShowPolygonForm,
  tempPolygon
}) => {
  const { goToCoordinates, countryCoordinates } = useMapStore();
  const { selectedPolygon, displayedPolygons, setSelectedPolygon, communeCoordinates, selectedRegion } = useContext(LocationsContext);
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const polygonsRefs = useRef<google.maps.Polygon[]>([]);
  const markerRef = useRef<any>(null); // Changed to any to support AdvancedMarkerElement
  const overlayRef = useRef<any>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const communePolygonRefs = useRef<google.maps.Polygon[]>([]);
  const goToMarkerRef = useRef<any>(null); // Use any to avoid strict TS version issues with AdvancedMarkerElement
  const [isEditingVertices, setIsEditingVertices] = useState(false);

  // State for the custom control div
  const [controlDiv, setControlDiv] = useState<HTMLDivElement | null>(null);

  // Initialize Google Map
  useEffect(() => {
    if (!mapRef.current) {
      const mapContainer = document.getElementById("map-container");
      if (mapContainer) {
        mapRef.current = new window.google.maps.Map(mapContainer, {
          ...DEFAULT_MAP_OPTIONS,
          center: goToCoordinates || DEFAULT_MAP_OPTIONS.center,
          fullscreenControl: true, // Ensure fullscreen control is enabled
        });

        // Create a div for the custom control
        const controlDivEl = document.createElement('div');
        // Prevent map clicks from passing through
        (controlDivEl as any).index = 1;
        controlDivEl.style.paddingTop = '10px';
        controlDivEl.addEventListener('click', (e) => {
          e.stopPropagation(); // Stop clicks from hitting the map
        });

        // Add to map controls
        mapRef.current.controls[window.google.maps.ControlPosition.TOP_CENTER].push(controlDivEl);
        setControlDiv(controlDivEl);

        // Create a CENTER control for overlays (PolygonForm)
        const centerControlEl = document.createElement('div');
        console.log(centerControlEl);
        // Create a dedicated Portal Root for generic overlays (Forms, Alerts)
        // We place it in TOP_LEFT (index 0) and make it 0-size but overflow visible
        // This allows us to use absolute positioning relative to the map frame
        const portalRoot = document.createElement('div');
        portalRoot.id = 'map-portal-root';
        (portalRoot as any).index = 0;
        portalRoot.style.width = '0px';
        portalRoot.style.height = '0px';
        portalRoot.style.overflow = 'visible';
        portalRoot.style.position = 'absolute';
        portalRoot.style.top = '0';
        portalRoot.style.left = '0';
        portalRoot.style.zIndex = '9999';

        mapRef.current.controls[window.google.maps.ControlPosition.TOP_LEFT].push(portalRoot);

        if (onMapReady) {
          onMapReady();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map center when goToCoordinates changes
  useEffect(() => {
    let isActive = true;
    const updateMarker = async () => {
      if (mapRef.current && goToCoordinates) {
        mapRef.current.panTo(goToCoordinates);
        mapRef.current.setZoom(15);
        
        // Cleanup previous marker
        if (goToMarkerRef.current) {
          goToMarkerRef.current.map = null;
        }

        try {
          // Import marker library dynamically (modern way, prevents deprecation warnings)
          const { AdvancedMarkerElement } = await window.google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
          
          if (!isActive) return;

          const pinContainer = document.createElement("div");
          pinContainer.className = "flex items-center justify-center";
          pinContainer.innerHTML = `
            <div class="text-red-600 drop-shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
          `;

          goToMarkerRef.current = new AdvancedMarkerElement({
            position: goToCoordinates,
            map: mapRef.current,
            title: "Ubicación buscada",
            content: pinContainer,
          });
        } catch (error) {
          console.error("Error loading AdvancedMarkerElement:", error);
        }
      }
    };
    updateMarker();

    return () => {
      isActive = false;
    };
  }, [goToCoordinates]);

  // Positioning and Reset: Handle transitions between Country, Region, and initial load
  useEffect(() => {
    if (!mapRef.current || !countryCoordinates?.center) return;

    if (selectedRegion) {
      // User selected a region, pan to country center at a wider zoom (9)
      console.log("Region selected, resetting to country center (Zoom 9)");
      mapRef.current.panTo(countryCoordinates.center);
      mapRef.current.setZoom(11);
    } else {
      // No region selected (initial load or cleared), use default country view (Zoom 12)
      console.log("No region selected, setting default country view (Zoom 12)");
      mapRef.current.panTo(countryCoordinates.center);
      mapRef.current.setZoom(12);
    }
  }, [selectedRegion, countryCoordinates]);

  // Draw Commune Boundary when area is available
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear previous commune boundaries
    if (communePolygonRefs.current.length > 0) {
      communePolygonRefs.current.forEach(poly => poly.setMap(null));
      communePolygonRefs.current = [];
    }

    if (communeCoordinates?.area) {
      const area = communeCoordinates.area;
      console.log('GoogleMap: communeCoordinates area:', area);
      
      let polygonsToDraw: any[] = [];

      // Normalize input to an array of polygon coordinates (each being an array of rings)
      if (area.type === 'MultiPolygon') {
        // MultiPolygon: coordinates is Array<Array<Array<[number, number]>>>
        // i.e. [ Polygon1, Polygon2, ... ] where PolygonX is [ Ring1, Ring2... ]
        polygonsToDraw = area.coordinates;
      } else if (area.type === 'Polygon') {
        // Polygon: coordinates is Array<Array<[number, number]>> (Array of rings)
        // We wrap it in an array to treat it uniformly as a list of polygons
        polygonsToDraw = [area.coordinates];
      }

      console.log('GoogleMap: polygonsToDraw count:', polygonsToDraw.length);

      polygonsToDraw.forEach((polygonRings) => {
        // polygonRings is Array of Rings. Each Ring is Array of coordinates [lng, lat].
        // Google Maps Polygon paths expects Array<LatLngLiteral> or MVCArray<LatLng>
        // The first ring is the outer boundary, subsequent rings are holes.
        
        // We verify the structure of the first ring to be safe
        const firstRing = polygonRings[0];
        if (!Array.isArray(firstRing)) return;

        const paths = polygonRings.map((ring: any[]) => {
          return ring.map((c: any) => ({
            lat: c[1],
            lng: c[0]
          }));
        });

        const communePolygon = new window.google.maps.Polygon({
          paths: paths,
          strokeColor: "#EAB308", // Yellow 500
          strokeOpacity: 0.5,
          strokeWeight: 2,
          fillColor: "#EAB308",
          fillOpacity: 0.15, // Faint yellow
          clickable: false, // Don't interfere with interaction
          zIndex: 0 // Below coverage polygons
        });

        communePolygon.setMap(mapRef.current);
        communePolygonRefs.current.push(communePolygon);
      });
    }
  }, [communeCoordinates]);

  useEffect(() => {
    setIsEditingVertices(false);
  }, [selectedPolygon]);

  // Center map on selected Commune using Coordinates from Service
  useEffect(() => {
    if (communeCoordinates?.center && mapRef.current) {
      const { latitude, longitude } = communeCoordinates.center;
      mapRef.current.panTo({ lat: latitude, lng: longitude });
      mapRef.current.setZoom(13); // Zoom for commune
    }
  }, [communeCoordinates]);

  // Handle Save
  const handleSave = async () => {
    if (polygonRef.current && onSaveGeometry && selectedPolygon?.id) {
      const path = polygonRef.current.getPath();
      const newCoordinates = path.getArray().map(latLng => ({
        lat: latLng.lat(),
        lng: latLng.lng()
      }));

      await onSaveGeometry(selectedPolygon.id, newCoordinates);
      setIsEditingVertices(false);
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    setIsEditingVertices(false);
  };

  // Manage Drawing Mode
  useEffect(() => {
    if (!mapRef.current) return;

    if (isDrawing) {
      // Init Drawing Manager
      drawingManagerRef.current = new window.google.maps.drawing.DrawingManager({
        drawingMode: window.google.maps.drawing.OverlayType.POLYGON,
        drawingControl: false,
        polygonOptions: {
          fillColor: '#3b82f6',
          fillOpacity: 0.3,
          strokeWeight: 2,
          clickable: false,
          editable: true,
          zIndex: 1
        },
        map: mapRef.current
      });

      // Listener
      window.google.maps.event.addListener(drawingManagerRef.current, 'overlaycomplete', (event: any) => {
        if (event.type === window.google.maps.drawing.OverlayType.POLYGON) {
          const polygon = event.overlay;
          const path = polygon.getPath().getArray().map((latLng: any) => ({
            lat: latLng.lat(),
            lng: latLng.lng()
          }));

          // Remove the drawn polygon because we will likely manage it via state/form
          polygon.setMap(null);

          const coordsArrays = path.map((p: any) => [p.lng, p.lat]);

          // Ensure the polygon is closed (first point === last point)
          if (coordsArrays.length > 0) {
            const firstPoint = coordsArrays[0];
            const lastPoint = coordsArrays[coordsArrays.length - 1];
            // Compare lng and lat
            if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
              coordsArrays.push([...firstPoint]);
            }
          }

          // GeoJSON expects [[[lng, lat], ...]] for Polygon (array of rings)
          const geoJsonCoordinates = [coordsArrays];

          const newTempPolygon = {
            type: 'include',
            status: true,
            coverageIds: [],
            polygon: {
              geometry: {
                coordinates: geoJsonCoordinates
              }
            },
            coordinates: geoJsonCoordinates // Top-level for some usages
          };

          if (setTempPolygon) setTempPolygon(newTempPolygon);
          if (setShowPolygonForm) setShowPolygonForm(true);
          if (setIsDrawing) setIsDrawing(false);

          if (onPolygonComplete) {
            // Pass generic structure expected by handlers
            onPolygonComplete(path);
          }
        }
      });

    } else {
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }
    }

    return () => {
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
      }
    };
  }, [isDrawing, onPolygonComplete, setTempPolygon, setShowPolygonForm, setIsDrawing]);

  // Render Temp Polygon (New)
  useEffect(() => {
    if (!mapRef.current || !tempPolygon) return;

    // Use safe access just in case
    const coords = tempPolygon.polygon?.geometry?.coordinates?.[0]?.map((c: any) => ({ lat: c[1], lng: c[0] }));

    if (coords) {
      const poly = new window.google.maps.Polygon({
        paths: coords,
        strokeColor: "#3b82f6", // Blue for new
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#3b82f6",
        fillOpacity: 0.35,
        editable: false,
        zIndex: 2
      });

      poly.setMap(mapRef.current);

      // We need to clean this up when tempPolygon changes or unmounts
      return () => {
        poly.setMap(null);
      };
    }
  }, [tempPolygon]);

  // Draw selected polygon and fit bounds
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear previous polygon
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    // Clear previous markers
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }

    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }

    if (selectedPolygon?.polygon?.geometry?.coordinates?.[0]) {
      const coordinates = selectedPolygon.polygon.geometry.coordinates[0].map((c: any) => ({
        lat: c[1],
        lng: c[0],
      }));

      const getPolygonColor = (type: string) => {
        if (type?.includes("exclude") || type?.includes("exclusion")) {
          return "#FF0000"; // Red for exclusion
        }
        return "#15803d"; // Dark green for inclusion (default)
      };

      const color = getPolygonColor(selectedPolygon.type);

      const newPolygon = new window.google.maps.Polygon({
        paths: coordinates,
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.35,
        editable: isEditingVertices, // Use state
        draggable: false
      });

      newPolygon.setMap(mapRef.current);
      polygonRef.current = newPolygon;

      // Fit bounds
      const bounds = new window.google.maps.LatLngBounds();
      coordinates.forEach((c: { lat: number, lng: number }) => bounds.extend(c));
      mapRef.current.fitBounds(bounds);

      const center = bounds.getCenter();
      
      // Render the marker asynchronously using the imported library
      let markerIsActive = true;
      window.google.maps.importLibrary("marker").then((markerLib: any) => {
        if (!markerIsActive) return;
        const { AdvancedMarkerElement } = markerLib;
        
        const infoContainer = document.createElement("div");
        infoContainer.innerHTML = `
          <div style="background-color: #2563eb; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border: 2px solid white;">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg>
          </div>
        `;

        markerRef.current = new AdvancedMarkerElement({
          position: center,
          map: mapRef.current,
          title: "Ver información",
          content: infoContainer
        });

        // Reattach listener to new marker type since it was created async
        if (markerRef.current) {
          markerRef.current.addListener("click", () => {
            if (infoWindowRef.current && mapRef.current && markerRef.current) {
              infoWindowRef.current.open(mapRef.current, markerRef.current);
            }
          });
        }
      }).catch(err => console.error("Error instantiating AdvancedMarkerElement on polygon:", err));

      // Add or update Label Overlay
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }

      class LabelOverlay extends window.google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement | null = null;
        private listener: google.maps.MapsEventListener | null = null;

        constructor(position: google.maps.LatLng) {
          super();
          this.position = position;
        }

        onAdd() {
          const div = document.createElement('div');
          div.style.position = 'absolute';
          div.style.cursor = 'pointer';

          div.innerHTML = `
            <div style="
              background-color: rgba(255, 255, 255, 0.95);
              backdrop-filter: blur(4px);
              padding: 6px 14px;
              border-radius: 9999px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
              border: 1px solid rgba(243, 244, 246, 0.8);
              display: flex;
              align-items: center;
              gap: 6px;
              transition: transform 0.2s;
              white-space: nowrap;
            ">
              <span style="font-size: 11px; font-weight: 600; color: #374151;">Ver más</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width: 12px; height: 12px;"><path d="M5 12h14"></path><path d="M12 5l7 7-7 7"></path></svg>
            </div>
          `;

          // Add click listener
          div.addEventListener('click', (e) => {
            e.stopPropagation(); // prevent map click
            if (infoWindowRef.current && mapRef.current && markerRef.current) {
              infoWindowRef.current.open(mapRef.current, markerRef.current);
            }
          });

          // Hover effect via JS since inline styles are tricky for psueodo-classes
          div.onmouseenter = () => { div.style.transform = 'scale(1.05) translateX(-50%)'; };
          div.onmouseleave = () => { div.style.transform = 'translateX(-50%)'; };

          this.div = div;

          const panes = this.getPanes();
          if (panes) {
            panes.floatPane.appendChild(div);
          }
        }

        draw() {
          if (!this.div) return;

          const overlayProjection = this.getProjection();
          const position = overlayProjection.fromLatLngToDivPixel(this.position);

          if (position) {
            this.div.style.left = position.x + 'px';
            this.div.style.top = (position.y + 25) + 'px'; // 25px below
            this.div.style.transform = 'translateX(-50%)';
          }
        }

        onRemove() {
          if (this.div) {
            if (this.div.parentNode) {
              this.div.parentNode.removeChild(this.div);
            }
            this.div = null;
          }
        }
      }

      const overlay = new LabelOverlay(center);
      overlay.setMap(mapRef.current);
      overlayRef.current = overlay;

      // Add InfoWindow
      const coverageIdsHtml = selectedPolygon.coverageIds && selectedPolygon.coverageIds.length > 0
        ? selectedPolygon.coverageIds.map((id: string) => `
    <span style="
      display: inline-block;
      padding: 2px 8px;
      background-color: #f3f4f6;
      color: #374151;
      border-radius: 4px;
      font-size: 11px;
      border: 1px solid #e5e7eb;
      margin-right: 4px;
      margin-bottom: 4px;
    ">${id}</span>
    `).join('')
        : '<span style="color: #9ca3af; font-size: 11px;">N/A</span>';

      const courierIdsHtml = selectedPolygon.courierIds && selectedPolygon.courierIds.length > 0
        ? selectedPolygon.courierIds.map((id: string) => `
    <span style="
      display: inline-block;
      padding: 2px 8px;
      background-color: #eff6ff;
      color: #1d4ed8;
      border-radius: 4px;
      font-size: 11px;
      border: 1px solid #dbeafe;
      margin-right: 4px;
      margin-bottom: 4px;
    ">${id}</span>
    `).join('')
        : '<span style="color: #9ca3af; font-size: 11px;">N/A</span>';

      const workingTimeHtml = selectedPolygon.workingTime && selectedPolygon.workingTime.length > 0
        ? selectedPolygon.workingTime.map((item: any) => {
          const DAYS_MAP: Record<number, string> = { 1: 'Lu', 2: 'Ma', 3: 'Mi', 4: 'Ju', 5: 'Vi', 6: 'Sá', 7: 'Do' };
          const daysLabels = (item.daysOfWeek || []).sort().map((d: number) => DAYS_MAP[d] || d).join(', ');
          return `
            <div style="font-size: 10px; color: #4b5563; background: #f9fafb; padding: 4px 8px; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 4px;">
              <span style="font-weight: 600; color: #2563eb;">${daysLabels}:</span> ${item.from} - ${item.to}
            </div>
          `;
        }).join('')
        : '<span style="color: #9ca3af; font-size: 11px;">N/A</span>';

      const coordinatesList = coordinates.map((c: { lat: number, lng: number }) => `[${c.lng}, ${c.lat}]`).join("<br/>");

      // Use SVG for copy icon to avoid dependency on external fonts/icons
      const copyIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;

      // Determine buttons based on mode
      let buttonsHtml = '';
      if (isEditingVertices) {
        buttonsHtml = `
    <div style="grid-column: span 2; text-align: center; color: #6b7280; font-size: 11px; font-style: italic; padding: 4px;">
      Modo edición activo. Use controles en el mapa.
    </div>
    `;
      } else {
        buttonsHtml = `
              <button id="btn-edit-polygon" style="
                 padding: 6px 12px;
                 background-color: #ffffff;
                 color: #2563eb;
                 border: 1px solid #2563eb;
                 border-radius: 4px;
                 cursor: pointer;
                 font-size: 12px;
                 font-weight: 500;
                 transition: all 0.2s;
              ">
                 Editar Info
              </button>
              <button id="btn-delete-polygon" style="
                 padding: 6px 12px;
                 background-color: #fee2e2;
                 color: #991b1b;
                 border: 1px solid #fecaca;
                 border-radius: 4px;
                 cursor: pointer;
                 font-size: 12px;
                 font-weight: 500;
                 transition: all 0.2s;
              ">
                 Eliminar
              </button>
              <button id="btn-edit-vertices" style="
                 grid-column: span 2;
                 padding: 6px 12px;
                 background-color: #f3f4f6;
                 color: #4b5563;
                 border: 1px solid #d1d5db;
                 border-radius: 4px;
                 cursor: pointer;
                 font-size: 12px;
                 font-weight: 500;
                 margin-top: 8px;
                 transition: all 0.2s;
              ">
                 Editar Vértices
              </button>
          `;
      }

      const contentString = `
    <div style="padding: 0; min-width: 260px; font-family: 'Inter', sans-serif;">
            <div style="background: #f3f4f6; padding: 10px 14px; border-bottom: 1px solid #e5e7eb; border-radius: 4px 4px 0 0;">
              <h3 style="margin: 0; font-weight: 600; font-size: 13px; color: #111827;">Información del Polígono ${isEditingVertices ? '(Editando Vértices)' : ''}</h3>
            </div>
            <div style="padding: 14px;">
              <div style="display: grid; grid-template-columns: auto 1fr; gap: 6px 16px; font-size: 12px; margin-bottom: 14px;">
                <span style="color: #6b7280; font-weight: 500;">ID:</span>
                <span style="color: #111827; font-weight: 600; text-overflow: ellipsis; overflow: hidden;">${selectedPolygon.id || selectedPolygon.name || 'N/A'}</span>
                
                <span style="color: #6b7280; font-weight: 500;">Cobertura:</span>
                <div style="display: flex; flex-wrap: wrap; gap: 4px; max-height: 60px; overflow-y: auto;">${coverageIdsHtml}</div>

                <span style="color: #6b7280; font-weight: 500;">Couriers:</span>
                <div style="display: flex; flex-wrap: wrap; gap: 4px; max-height: 60px; overflow-y: auto;">${courierIdsHtml}</div>
                
                <span style="color: #6b7280; font-weight: 500;">Tipo:</span>
                <span>
                  <span style="
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 9999px;
                    font-size: 11px;
                    font-weight: 600;
                    background-color: ${selectedPolygon.type === 'include' || selectedPolygon.type === 'inclusion' ? '#dcfce7' : '#fee2e2'};
                    color: ${selectedPolygon.type === 'include' || selectedPolygon.type === 'inclusion' ? '#166534' : '#991b1b'};
                  ">
                    ${selectedPolygon.type === 'include' || selectedPolygon.type === 'inclusion' ? 'INCLUSIÓN' : 'EXCLUSIÓN'}
                  </span>
                </span>
                
                <span style="color: #6b7280; font-weight: 500;">Estado:</span>
                <span style="color: ${selectedPolygon.status ? '#166534' : '#9ca3af'}; font-weight: 600;">
                  ${selectedPolygon.status ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </div>

              <div style="margin-top: 8px;">
                <span style="color: #6b7280; font-weight: 500; font-size: 12px; display: block; margin-bottom: 4px;">Horario de Trabajo:</span>
                <div style="max-height: 100px; overflow-y: auto;">
                  ${workingTimeHtml}
                </div>
              </div>
  
              <div style="margin-top: 14px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                 ${buttonsHtml}
              </div>
  
              <div style="margin-top: 14px; border-top: 1px solid #e5e7eb; padding-top: 10px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                  <strong style="font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px;">Coordenadas</strong>
                  <button id="btn-copy-coords" title="Copiar coordenadas" style="
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #6b7280;
                    padding: 2px;
                    display: flex;
                    align-items: center;
                    transition: color 0.2s;
                  ">
                    ${copyIconSvg}
                  </button>
                </div>
                <div style="
                  font-size: 10px;
                  color: #4b5563;
                  background: #f9fafb;
                  padding: 8px;
                  border: 1px solid #e5e7eb;
                  border-radius: 4px;
                  max-height: 80px;
                  overflow-y: auto;
                  font-family: monospace;
                  line-height: 1.4;
                ">
                  ${coordinatesList}
                </div>
              </div>
            </div>
          </div>
    `;

      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }

      infoWindowRef.current = new window.google.maps.InfoWindow({
        content: contentString,
      });

      // Add domready listener to attach events
      infoWindowRef.current.addListener('domready', () => {
        const editBtn = document.getElementById('btn-edit-polygon');
        const deleteBtn = document.getElementById('btn-delete-polygon');
        const copyBtn = document.getElementById('btn-copy-coords');
        const editVerticesBtn = document.getElementById('btn-edit-vertices');

        if (editBtn) {
          editBtn.addEventListener('click', () => {
            if (onEdit) onEdit(selectedPolygon);
            infoWindowRef.current?.close();
          });
        }

        if (deleteBtn) {
          deleteBtn.addEventListener('click', () => {
            if (onDelete && selectedPolygon.id) onDelete(selectedPolygon.id);
            infoWindowRef.current?.close();
          });
        }

        if (editVerticesBtn) {
          editVerticesBtn.addEventListener('click', () => {
            setIsEditingVertices(true);
            infoWindowRef.current?.close(); // Close window to unveil map
          });
        }

        if (copyBtn) {
          copyBtn.addEventListener('click', () => {
            // Return standard GeoJSON format [[lng, lat]]
            const textToCopy = JSON.stringify(coordinates.map((c: any) => [c.lng, c.lat]));
            navigator.clipboard.writeText(textToCopy).then(() => {
              const originalColor = copyBtn.style.color;
              copyBtn.style.color = '#10b981'; // Green color
              setTimeout(() => {
                copyBtn.style.color = originalColor;
              }, 1000);
            }).catch(err => {
              console.error('Failed to copy: ', err);
            });
          });
        }
      });

      // We handle marker listener attaching inside the importLibrary block above,
      // so we don't need to try to attach it here where markerRef.current might still be null synchronously.

      // If isEditingVertices is true, we simply close the infoWindow (handled in click listener above), or ensure it stays closed.
      if (isEditingVertices && infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    }
  }, [selectedPolygon, onEdit, onDelete, onSaveGeometry, isEditingVertices]);

  // If we are not editing or don't have the control div yet, return null (but keep map alive!)
  // IMPORTANT: The portal should only render if isEditingVertices is true AND controlDiv exists.
  // We can't return null early above because it would unmount the hooks.
  // The map logic is inside effects, so it's safe.

  const renderEditControls = () => {
    if (!isEditingVertices || !controlDiv) return null;

    return createPortal(
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(4px)',
        padding: '8px 12px',
        borderRadius: '9999px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0,0,0,0.05)',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        fontFamily: "'Inter', sans-serif",
        pointerEvents: 'auto', // Ensure clicks work
        marginBottom: '10px' // some space from top
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          paddingLeft: '8px',
          borderRight: '1px solid #e5e7eb',
          paddingRight: '12px'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
          </svg>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
            Editando Vértices
          </span>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '6px 14px',
              backgroundColor: '#fff',
              color: '#4b5563',
              border: '1px solid #d1d5db',
              borderRadius: '9999px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '6px 14px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: '1px solid #2563eb',
              borderRadius: '9999px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              transition: 'all 0.2s',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Guardar
          </button>
        </div>
      </div>,
      controlDiv
    );
  };

  // Render multiple polygons (Show All)
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear previous list polygons
    polygonsRefs.current.forEach(p => p.setMap(null));
    polygonsRefs.current = [];

    // Prioritize prop polygons, fall back to context
    const polygonsToRender = polygons || displayedPolygons;

    if (polygonsToRender && polygonsToRender.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      polygonsToRender.forEach((polygon: any) => {
        // Skip if this is the currently selected polygon (drawn by the other effect)
        if (selectedPolygon && (polygon.id === selectedPolygon.id)) {
          return;
        }

        if (polygon?.polygon?.geometry?.coordinates?.[0]) {
          const coords = polygon.polygon.geometry.coordinates[0].map((c: any) => ({
            lat: c[1],
            lng: c[0]
          }));

          const isExclusion = polygon.type?.includes("exclude") || polygon.type?.includes("exclusion");
          const color = isExclusion ? "#FF0000" : "#15803d";

          const poly = new window.google.maps.Polygon({
            paths: coords,
            strokeColor: color,
            strokeOpacity: 0.6,
            strokeWeight: 1,
            fillColor: color,
            fillOpacity: 0.2, // Lighter opacity for "All" view
            clickable: true,
            zIndex: 1
          });

          poly.setMap(mapRef.current);
          polygonsRefs.current.push(poly);

          // Extend bounds
          coords.forEach((c: any) => bounds.extend(c));

          // Click to Select
          poly.addListener("click", () => {
            console.log("Clicked polygon from list:", polygon);
            if (setSelectedPolygon) {
              setSelectedPolygon(polygon);
            }
          });
        }
      });

      // Fit bounds only if we have polygons and it's not a drawing mode interaction?
      // Actually we probably want to fit bounds on load.
      if (!bounds.isEmpty()) {
        // Optional: Maybe only fit bounds if we have a significant number of polygons or initial load
        mapRef.current.fitBounds(bounds);
      }
    }
  }, [polygons, displayedPolygons, setSelectedPolygon, selectedPolygon]);

  return renderEditControls();
};

export default MapComponent;